import type { WorkflowIssue } from "./workflow-validation"
import type { Edge, Node } from "@xyflow/react"

type InteractionMode = "one_shot_create" | "chat_edit"

type BuildWorkflowBuilderMessageParams = {
  workflowId: string
  userPrompt: string
  nodes: Node[]
  edges: Edge[]
  issues: WorkflowIssue[]
  selectedIssue?: WorkflowIssue
  interactionMode?: InteractionMode
}

/**
 * Build the user message for the backend `workflow_builder` agent.
 *
 * The node/operation/handle schema, the allowed node types and their data
 * fields, and the authoring reminders now live in the backend system prompt
 * (agent_runtime/config/prompts/workflow_builder.md) — the single source of
 * truth (#40 / #1489), so they stay in sync with graph_builder as new node types
 * land. This message therefore carries only the *dynamic* context: the current
 * graph (nodes/edges + their ids) and any validation issues to address. Live
 * editor file-rules/validation stay in the frontend.
 */
export function buildWorkflowBuilderUserMessage({
  workflowId,
  userPrompt,
  nodes,
  edges,
  issues,
  selectedIssue,
  interactionMode = "chat_edit",
}: BuildWorkflowBuilderMessageParams): string {
  const context = {
    workflow_id: workflowId,
    interaction_mode: interactionMode,
    current_graph: { nodes, edges },
    current_node_ids: nodes.map((node) => node.id),
    current_edge_ids: edges.map((edge) => edge.id),
    validation_issues: issues,
    ...(selectedIssue ? { selected_issue: selectedIssue } : {}),
  }

  return [
    "<workflow_builder_context>",
    JSON.stringify(context),
    "</workflow_builder_context>",
    "",
    userPrompt,
  ].join("\n")
}
